import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface CommercialRow {
  id: number;
  nom: string;
  prenom: string;
  entrepot_id: number | null;
  station_id: number | null;
  user_id: number | null;
  entrepot_libelle?: string | null;
  code_station?: string | null;
  station_nom?: string | null;
  user_name?: string | null;
  user_email?: string | null;
  created_at?: string;
}

export interface CommercialPayload {
  nom: string;
  prenom: string;
  entrepot_id: number;
  station_id: number;
  user_id: number;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseCommercialService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  private baseUrl(): string {
    return `${lcApiBase(this.authService)}/api/commerciaux`;
  }

  listForCurrentStation(): Observable<CommercialRow[]> {
    const stationId = lcStationId(this.authService);
    const url = `${this.baseUrl()}/station/${stationId}`;
    return this.http.get<{ success: boolean; data?: CommercialRow[]; error?: string }>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.data)) {
          throw new Error(res?.error || 'Réponse commerciaux invalide');
        }
        return res.data;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des commerciaux.'))
      )
    );
  }

  create(body: CommercialPayload): Observable<CommercialRow> {
    return this.http
      .post<{ success: boolean; data?: CommercialRow; error?: string }>(this.baseUrl(), body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Création commercial échouée');
          }
          return res.data;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la création du commercial.'))
        )
      );
  }

  update(id: number, body: Partial<CommercialPayload>): Observable<CommercialRow> {
    const url = `${this.baseUrl()}/${id}`;
    return this.http.put<{ success: boolean; data?: CommercialRow; error?: string }>(url, body).pipe(
      map((res) => {
        if (!res?.success || !res.data) {
          throw new Error(res?.error || 'Mise à jour commercial échouée');
        }
        return res.data;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour du commercial.'))
      )
    );
  }

  delete(id: number): Observable<void> {
    const url = `${this.baseUrl()}/${id}`;
    return this.http.delete<{ success: boolean; error?: string }>(url).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Suppression commercial échouée');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la suppression du commercial.'))
      )
    );
  }

  buildPayload(entrepotId: string | number, nom: string, prenom: string): CommercialPayload {
    const userId = this.authService.getBackUserId();
    const stationId = lcStationId(this.authService);
    if (userId == null) {
      throw new Error('Session incomplète : utilisateur back caisse manquant.');
    }
    const entrepot_id = Number(entrepotId);
    if (!Number.isFinite(entrepot_id) || entrepot_id <= 0) {
      throw new Error('Sélectionnez un entrepôt.');
    }
    return {
      nom: nom.trim(),
      prenom: prenom.trim(),
      entrepot_id,
      station_id: stationId,
      user_id: userId,
    };
  }
}
